import type { QualityCheckDetailDto, QualityPackCheckRowDto } from '@artic/api-client';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Breadcrumb name lookups for the Quality Packs routes.
 *
 * The pack/check queries are partner-scoped, so their query keys embed a
 * partner id/name that a static breadcrumb resolver can't reconstruct (there
 * is no hook context inside `staticData.breadcrumb`). These helpers locate the
 * already-cached response by the work-order / job-card portion of the key
 * instead — the same data the pack and check pages have loaded — and return
 * `null` until it is present so the breadcrumb stays empty rather than flashing
 * a placeholder (matching the rest of the breadcrumb system).
 */

/** The Orval response envelope (`{ status, data }`) the quality hooks resolve to. */
interface QualityResponse<T> {
  status: number;
  data: T;
}

function findCachedResponse<T>(
  queryClient: QueryClient,
  keyMatches: (firstKey: string) => boolean,
): T | undefined {
  const entries = queryClient.getQueriesData<unknown>({
    predicate: (query) => {
      const first = query.queryKey[0];
      return typeof first === 'string' && keyMatches(first);
    },
  });
  for (const [, value] of entries) {
    const response = value as QualityResponse<T> | undefined;
    if (response?.status === 200) return response.data;
  }
  return undefined;
}

/**
 * Work-order name for the pack breadcrumb, read from the cached
 * `/partners/:id/quality-packs/:workOrderId/checks` response (loaded by the
 * pack page). Returns null until the checks load (or the WO has none).
 */
export function readWorkOrderName(queryClient: QueryClient, workOrderId: string): string | null {
  const rows = findCachedResponse<QualityPackCheckRowDto[]>(queryClient, (key) =>
    key.endsWith(`/quality-packs/${workOrderId}/checks`),
  );
  return rows?.[0]?.work_order_name ?? null;
}

/**
 * Work-order + check names for the check breadcrumb, read from the cached
 * `/quality/quality-check-detail/:partner/:jobCardId/:order` response (loaded
 * by the check page). Returns null until the detail loads.
 */
export function readCheckBreadcrumbNames(
  queryClient: QueryClient,
  jobCardId: string,
  orderNumber: string,
): { workOrderName: string; checkName: string } | null {
  // The check-detail query keys the order as a number (`Number(orderNumber)`),
  // so normalise the route param to match (e.g. a "/03" URL → "3").
  const order = Number(orderNumber);
  const detail = findCachedResponse<QualityCheckDetailDto>(
    queryClient,
    (key) =>
      key.startsWith('/quality/quality-check-detail/') && key.endsWith(`/${jobCardId}/${order}`),
  );
  if (!detail) return null;
  return {
    workOrderName: detail.check.work_order_name,
    checkName: detail.check.in_process_check_name,
  };
}
